/*
 * fragments.ts
 *
 * Created by Dr. Maximillian Dornseif 2021-11-04 in huWaWi3 23.3.2
 * Copyright (c) 2021-2023 HUDORA GmbH
 */

import { disableFragmentWarnings, gql } from 'graphql-tag'

if (import.meta.env.MODE === 'development') {
  disableFragmentWarnings()
}

export const ANMERKUNG_FRAGMENT = gql`
  fragment anmerkungFragment on EntityAnmerkung {
    id

    text
    forDesignator

    designator
    updated_at
    created_at
  }
`

export const KUNDE_FRAGMENT = gql`
  fragment kundeFragment on HwKunde {
    id

    designator
    name
    bezeichner
    ist_aktiv
    _rev

    adresse {
      id
      name
      zusatz
      strasse
      land
      plz
      ort
      iln
      tel
      email
    }

    intern
    kuerzel
    gruppennr
    verbandsnr
    mitgliedsnr_verband
    unsere_lieferantennr
    intern
    steuerreferenznr
    kreditlimit
    zuteilungssperre

    nettotage
    skontotage
    skontoprozent
    kundenrabatt_prozent
    kundenrabatt_text
    logistikzuschlag_prozent
    freihausgrenze
    sammelrechnung

    created_at
    updated_at
  }
`

export const CRM_FRAGMENT = gql`
  fragment crmFragment on H3KundeInfo {
    id

    designator
    forDesignator

    abschlag_fob
    abschlag_lager
    abschlag_ziel
    alternative_kundennr
    alternative_kundennrs
    entsorgernummer
    entsorgungssystem
    hudora_express
    lastschrift
    segmentierung
    created_at
    updated_at
    _rev
  }
`

export const EDI_FRAGMENT = gql`
  fragment ediFragment on HwKundeEdiConfig {
    id
    designator
    created_at
    updated_at
    _rev

    kundennr
    belegsprache
    beleg_regexp

    orders {
      inbound_iln
    }
    ordrsp {
      inhouse_enabled
      inhouse_path
      auto_bb
      email
      edi
      render_enabled
      render_path
      render_format
      render_template_id
      callback
    }
    ostrpt {
      inhouse_enabled
      inhouse_path
      auto_as
      email
      edi
      render_enabled
      render_path
      render_format
      render_template_id
      callback
    }
    desadv {
      email
      edi
      inhouse_enabled
      inhouse_path
      render_enabled
      render_path
      render_format
      render_template_id
      callback
    }
    invoic {
      doppeldruck
      letter
      print
      einkaeufer_iln
      tech_adresse {
        name
        zusatz
        strasse
        land
        plz
        ort
        tel
        iln
        email
      }
      email_tech
      skonto_als_abschlag

      email
      edi
      inhouse_enabled
      inhouse_path
      render_enabled
      render_path
      render_format
      render_template_id
      callback
    }
  }
`

export const KUNDEN_ARTNR_FRAGMENT = gql`
  fragment artnrFragment on HwKundenArtNr {
    id

    designator
    artnr
    kundennr
    artnr_kunde
    name
    ist_aktiv
    ist_gesperrt
    created_at
    updated_at
    _rev
  }
`
export const KUNDEN_PREIS_FRAGMENT = gql`
  fragment preisFragment on HwKundenPreis {
    id

    designator
    artnr
    kundennr
    preis
    name
    ist_aktiv
    created_at
    updated_at
    _rev
  }
`

export const VERSANDANWEISUNG_FRAGMENT = gql`
  fragment versandanweisungFragment on HwVersandanweisung {
    id
    designator
    _rev
    created_at
    updated_at

    referenz
    kundennr
    avisieren_tage
    avisieren_unter
    hebebuehne
    selbstabholer
    info_lager
    info_spediteur
    versandart_unfrei
    ccg_i
    ist_aktiv
    refs
  }
`

export const LIEFERADRESSE_FRAGMENT = gql`
  fragment laFragment on HwLieferadresse {
    id

    designator
    kundennr
    name
    strasse
    land
    plz
    ort
    iln
    ist_aktiv
    zusatz
    tel
    email
    created_at
    updated_at
    versandanweisung {
      ...versandanweisungFragment
    }
    _rev
  }
  ${VERSANDANWEISUNG_FRAGMENT}
`

export const PRODUKT_FRAGMENT = gql`
  fragment produktFragment on H3Produkt {
    id

    designator
    created_at
    updated_at

    ist_aktiv
    name
    kategorie
    ztNr
    einheit
    istSperrgut
  }
`

export const PIM_FRAGMENT = gql`
  fragment produktInfoFragment on H3ProduktInfo {
    id

    designator
    created_at
    updated_at
    forDesignator

    warenklasse
    maximales_nutzergewicht
    name_en
    angebotstext_de
    angebotstext_en
    reklame
    reklameEn
    reklameKurz
    bulletPoints
    features
    warnsaetze
    istSpielzeug
    normvorgaben
    vonAlter
    bisAlter
    battTyp
    battGewicht
    battMng
    elektroG
    bereich
    betreuer
    spezifikation_einkauf
  }
`

export const ITEM_FRAGMENT = gql`
  fragment itemFragment on H3Item {
    id

    designator
    name
    ean
    kunde
    gewicht
    forDesignator
    produktname
    zusatz
    mehrkarton
    zuteilungssperre
    lieferantenNr

    updated_at
    created_at
    ist_aktiv
  }
`

export const ITEM_PIM_FRAGMENT = gql`
  fragment itemInfoFragment on H3ItemInfo {
    id
    designator
    created_at
    updated_at
    forDesignator

    marke
    einstufung
    dimL
    dimB
    dimH
    keine_nachdisposition
    auslaufartikel
    kataloge
    mindestbestand
    artworksprachen
    moq
    itemname_en
    namenzusatz_en
    zusatz_spezifikationen_en
    preismodell
    features
    ce
    gs
    pantone
    reinzeichnung_kennzeichnung_stand
    reinzeichnung_anleitung_stand
    reinzeichnung_verpackung_stand
    barcode_kunde
    artnr_kunde
    nachfolger
    maximales_nutzergewicht
  }
`

export const SKUSUM_FRAGMENT = gql`
  fragment skuSumFragment on H3SkuSum {
    nrs

    gwPkg
    volPkg

    keinEx
    volEx
    mngEx
    gwEx
    dimEx

    hPal
    xlPal
    mngPal

    herkunftsland
    gwPappe
    gwPal
    gwKunststoff
    fobHafen
  }
`

export const SKUM_FRAGMENT = gql`
  fragment skuMasterFragment on H3SkuMaster {
    id

    designator
    name
    forDesignator
    zusatz
    itemname
    eanEx

    updated_at
    created_at
    ist_aktiv
  }
`

export const SKUM_PIM_FRAGMENT = gql`
  fragment skuMasterInfoFragment on H3SkuMasterInfo {
    id

    designator
    created_at
    updated_at
    forDesignator

    pkgInfo
  }
`
export const SKU_FRAGMENT = gql`
  fragment skuFragment on H3Sku {
    id

    designator
    forDesignator
    mng20ft
    mng40ft
    mng40fthq
    mngPal
    hPal
    gwPal
    xlPal
    mngEx
    gwEx
    dimEx
    volEx
    keinEx
    herkunftsland
    mastername
    zusatz
    name

    ist_aktiv
    updated_at
    created_at
  }
`
export const SKU_PIM_FRAGMENT = gql`
  fragment skuInfoFragment on H3SkuInfo {
    id

    designator
    created_at
    updated_at
    forDesignator

    pkgGewicht
    pkgGroesseB
    pkgGroesseH
    pkgGroesseL
    pkgInfo
    gwPappe
    gwKunststoff
    ve1
    ve1Gewicht
    ve1GroesseB
    ve1GroesseH
    ve1GroesseL
    ve1Mng
    proLageEx
    fobHafen
    lieferantenNr
    lieferantenArtNr
  }
`
export const QX_PREIS_FRAGMENT = gql`
  fragment qxPreisFragment on HwIcQxPreis {
    id
    _rev
    designator
    preis
    name
    ist_aktiv
    beschreibung
    created_at
    updated_at
  }
`
export const BESTELLPOSITION_FRAGMENT = gql`
  fragment bestellpositionFragment on HwBestellPosition {
    id
    bestellnr
    posnr
    erledigt
    anlieferdatum
    artnr
    lager
    lieferant_name
    lieferantennr
    markt_kurs
    menge_bestellt
    menge_geliefert
    menge_offen
    name
    updated_at
    verschiffung
    waehrung
    exportkartontext1
    exportkartontext2
    created_at
    updated_at
    # Diese Beiden Spalten werden cniht automatisch mit aufgelistet:
    # preis
    # kurs
  }
`
export const AUFTRAGSENTWURF_FRAGMENT = gql`
  fragment auftragsEntwurfFragment on H3AuftragsEntwurf {
    _keyStr
    _rev
    id
    created_at
    updated_at
    designator

    kundennr
    auftragsnr_kunde
    anlieferdatum
    zeitfenster

    infotext_intern
    infotext_kunde
    infotext_logistik
    created_via
    erfasst_von
    transportId
    referenzen

    positionen {
      menge
      itemnr
      posnr_kunde
      itemnr_kunde
      infotext_kunde
      infotext_intern
    }

    adrnr
    dropshipping
    dropshippingAnschrift {
      name
      zusatz
      strasse
      plz
      ort
      land
      iln
      email
      tel
    }

    lager

    keineWarnungen
    valide
    errors
    warnings
    auto
    erledigt
    auftragsnr

    created_at
    updated_at
  }
`
export const AUFTRAGSENTWURFPOSITIONEN_FRAGMENT = gql`
  fragment auftragsEntwurfPositionenFragment on H3AuftragsEntwurf {
    positionen {
      menge
      itemnr
      posnr_kunde
      itemnr_kunde
      infotext_kunde
      infotext_intern
    }
  }
`

export const AUFTRAG_FRAGMENT = gql`
  fragment auftragFragment on HwAuftrag {
    id

    auftragsnr
    auftragsnr_kunde
    anlieferdatum_bis
    anlieferdatum_von

    erledigt
    immutable
    status
    teillieferung_gewuenscht
    zuteilungssperre
    muss_genehmigt_werden
    bestellbestaetigung
    storniert

    orderid_client

    auftragsrabatt_prozent
    auftragsrabatt_text
    empfaengernr
    verbandsnr
    infotext_intern
    infotext_kunde
    kundenlieferschein_vorhanden
    kundenrabatt_prozent
    kundenrabatt_text

    lager
    name

    positionsanzahl

    wert_netto
    wert
    wert_offen

    auftragsbestaetigungen
    kommiauftraege
    lieferscheine
    rechnungen
    referenzen

    warenempfaenger {
      id
      name
      zusatz
      strasse
      land
      plz
      ort
      tel
      email
      iln
    }

    zuteilung_gescheitert_at

    created_via
    erfasst_von
    _keyStr
    _rev

    updated_at
    created_at
  }
`
export const AUFTRAGSPOSITION_FRAGMENT = gql`
  fragment auftragspositionFragment on HwAuftragsPosition {
    id

    bartnr
    lager
    menge_offen
    menge_geliefert
    menge_bestellt
    posnr
    preis
    preisherkunft
    infotext_kunde
    erledigt
    storniert
    empfaengernr
    auftragsnr

    _rev
    _keyStr
    updated_at
    created_at
  }
`

export const ZUSATZLEISTUNG_FRAGMENT = gql`
  fragment zusatzleistungFragment on HwAuftragsZusatzleistung {
    id
    _rev
    _keyStr

    artnr
    name
    preis
    erledigt
    storniert
    infotext_kunde
    posnr
    empfaengernr
    auftragsnr
    designator
    created_at
    updated_at
  }
`

export const RESERVIERUNG_FRAGMENT = gql`
  fragment reservierungFragment on HwReservierung {
    id
    _keyStr
    _rev
    designator

    reservierungsnr
    anlieferdatum_bis
    zeitfenster
    anlieferdatum_von
    name
    empfaengernr
    auftragsnr_kunde
    erledigt
    erledigt_am
    infotext_intern
    infotext_kunde
    created_at
    updated_at
    # erfasst_von
    # updated_by
  }
`
export const RESERVIERUNGSKOPF_FRAGMENT = gql`
  fragment reservierungKopfFragment on HwReservierungsKopf {
    id
    _keyStr
    _rev
    designator

    reservierungsnr
    anlieferdatum_bis
    zeitfenster
    anlieferdatum_von
    name
    empfaengernr
    auftragsnr_kunde
    erledigt
    erledigt_am
    infotext_intern
    infotext_kunde
    created_at
    updated_at
    # erfasst_von
    # updated_by
  }
`

export const RESERVIERUNGSPOSITION_FRAGMENT = gql`
  fragment resevierungspositionFragment on HwReservierungsPosition {
    id
    _keyStr
    _rev

    reservierungsnr
    anlieferdatum_von
    zeitfenster
    bartnr
    empfaengernr
    name
    menge_bestellt
    menge_geliefert
    menge_offen
    preis
    erledigt
    erfasst_von

    designator
    posnr
    artnr
    anlieferdatum_bis
    updated_at
    created_at
  }
`

export const KOMMI_FRAGMENT = gql`
  fragment kommiAuftragFragment on HwKommiAuftrag2 {
    id

    designator
    lager
    prioritaet
    status
    empfaengernr
    name
    auftragsnr
    anlieferdatum_von
    anlieferdatum_bis
    zeitfenster
    auftragsnr_kunde
    infotext_kunde
    rueckgemeldet_at
    wert
    status

    created_at
    updated_at
  }
`

export const LAGER_FRAGMENT = gql`
  fragment lagerFragment on HwLager {
    name
    ist_aktiv

    autoAuftrag
    vorabLS
    kommiXml
    kommiCsv
    dropboxLS

    id
    designator
    created_at
    updated_at
  }
`

export const BESTANDSBUCHSUMME_FRAGMENT = gql`
  fragment bestandsbuchsummeFragment on HwIcBestandsbuchSumme {
    id
    created_at
    updated_at

    sku
    artnr
    abgangsmenge
    adw
    erster_zugang
    bewegungen
    designator
    gebucht_bis_datum
    gebucht_bis_ts
    lager_menge
    lager
    lager_wert
    letzter_abgang
    zugangsmenge
  }
`

export const BESTANDSBUCH_FRAGMENT = gql`
  fragment bestandsbuchFragment on HwIcBestandsbuchEintrag {
    buchungsnr
    lager
    menge
    artnr
    charge
    ausloeser
    beleg
    datum
    index
    info
    vorgang
    vorgang_posnr
    menge_nach_verbuchung
    menge_vor_verbuchung
    verbucht
    verbucht_at

    # Diese Felder werden nciht automatisch mit ausgewählt:
    wert
    lagerwert_vor_verbuchung
    lagerwert_nach_verbuchung

    vindex
    vnr
    vorhergehender_bestandsbucheintrag

    id
    designator
    created_at
    updated_at
  }
`
export const ZUTEILUNG_FRAGMENT = gql`
  fragment zuteilungFragment on HwIcZuteilung {
    artnr
    artnr_auftrag
    auftragsnr
    created_at
    designator
    id
    komminr
    lager
    menge
    posnr_auftrag
    updated_at
  }
`
export const WERTKORREKTUR_FRAGMENT = gql`
  fragment wertkorrekturFragment on HwIcWertkorrektur {
    artnr
    bartnr
    created_at
    designator
    id
    laeger
    soll_adw
    updated_at
    wertaenderung
  }
`

export const INVENTURBESTAETIGUNG_FRAGMENT = gql`
  fragment intventurBestaetigungFragment on HwIcIntventurBestaetigung {
    id
    designator
    buchungsnr_korrektur
    buchungsnr_bestaetigung
    inventur
    lager
    artnr
    zaehlbestand
    abweichung_menge
    abweichung_wert
    status
    verbucht
    bestaetigt
    info
    quelle
    updated_at
    created_at
  }
`

export const USER_FRAGMENT = gql`
  fragment userFragment on AaaUser {
    id
    _rev
    designator
    user
    uid
    name
    enabled
    created_at
    updated_at
  }
`
export const ROLE_FRAGMENT = gql`
  fragment roleFragment on AaaRole {
    id
    _rev
    created_at
    designator
    name
    uids
    permissions
    updated_at
  }
`

export const PERMISSION_FRAGMENT = gql`
  fragment permissionFragment on AaaPermission {
    name
    ist_aktiv
    concern
    created_at
    designator
    fieldName
    fileName
    id
    _rev
    operation
    resolverName
    returnType
    subconcern
    type
    updated_at
  }
`

export const GRUPPE_FRAGMENT = gql`
  fragment gruppeFragment on HwKundenGruppe {
    id
    _rev
    designator
    name
    vertreter
    branche
    ist_aktiv
    created_at
    updated_at
  }
`

export const PREIS_FRAGMENT = gql`
  fragment preisLFragment on HwPreis {
    id
    _rev
    designator
    bartnr
    fh
    uvp
    name
    ist_aktiv
    basis_name
    updated_at
    created_at
  }
`

export const TEMPLATECONFIG_FRAGMENT = gql`
  fragment configTemplateFragment on H3ConfigTemplate {
    id
    _rev
    designator
    art
    templateId
    ist_aktiv
    created_at
    updated_at
  }
`
export const BELEG_FRAGMENT = gql`
  fragment belegFragment on Beleg {
    designator
    belegart
    belegdatum
    name
    kundennr
    empfaenger
    created_at
  }
`
